import { createContext, useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { get, onValue, ref, set } from 'firebase/database';
import { auth, db } from '../firebase';
import {
  RENTAL_EQUIPMENT,
  DEFAULT_BOOKING_WINDOW_DAYS,
  DEFAULT_NEXT_DAY_GRID_END_HOUR,
  DEFAULT_CURRENCY_SYMBOL,
  DEFAULT_CURRENCY_DESCRIPTION,
  DEFAULT_PARTIAL_PAYMENT_PERCENT,
  CONFIG_CACHE_MAX_AGE_MS,
  DEFAULT_THEME_COLORS,
  DEFAULT_UI_DESIGN_VERSION,
  DEFAULT_RATE_SEPARATOR_DESIGN,
  DEFAULT_BROWSER_PROMPT_VERSION,
  DEFAULT_BOOKING_SETTINGS,
} from '../constants/bookingConst';
import { cacheLogoUrl, getCachedLogoUrl } from '../utils/bookingUtils';
import { readCachedConfig, writeCachedConfig, clearCachedConfig } from '../utils/configCacheUtil';
import { useAuthStatus } from './AuthContext';
import { isSuperAdmin } from '../utils/authServiceUtil';
// Generated at deploy time by scripts/snapshotConfig.js — the landing page's
// share of /config (allowlisted fields only, no images), or {} when none.
import CONFIG_SNAPSHOT from '../generated/configSnapshot.json';
import { applySnapshotImages } from '../utils/snapshotImageUtil';
import { readBuildSnapshot } from '../utils/buildBrandingUtil';
import { isValidHex, hexToRgb, hexToRgba, shadeHex } from '../utils/colorUtil';
import { buildSlotColorCssVars } from '../utils/slotButtonColorUtil';

// Default configuration values
const DEFAULT_CONFIG = {
  courtName: 'PickleBall Court',
  // System — when false, guests skip the marketing landing page entirely
  // and land straight on the court/time grid (see App.jsx's showLandingPage
  // logic). Edited from Config -> System.
  showLandingPage: true,
  // Court Reminders — "Show court rules before checkout". When true, the
  // guest must read and accept courtRemindersText (CourtRulesSheet) before
  // checkout opens. Off by default so existing clubs keep their current flow.
  showCourtRulesBeforeCheckout: false,
  // Court Reminders — the free text that popup shows. Empty skips the popup.
  courtRemindersText: '',
  // System — whether an idle admin is signed out automatically. True keeps
  // the 30-minute idle window (ADMIN_IDLE_TIMEOUT_MS in authServiceUtil.js);
  // false keeps an admin signed in on their device until they log out by
  // hand, which is what a club running the dashboard on its own front-desk
  // machine wants. Read as `!== false` everywhere, so a config saved before
  // this setting existed still times out.
  adminIdleTimeoutEnabled: true,
  // System — whether admins can open past dates on the Manage availability
  // grid (to record bookings that were never entered). Off by default and
  // read as `=== true` everywhere (see isPastDateViewEnabled), so a club only
  // gets past dates on the grid once it opts in.
  adminViewPastDatesEnabled: false,
  // System — which statuses the Manage availability dropdown offers, as
  // { [status]: boolean }. Left empty so every status uses its
  // defaultEnabled in ADMIN_GRID_STATUS_OPTIONS (all on except Closed) until
  // the club saves its own choice — see getEnabledGridStatuses.
  adminGridStatusOptions: {},
  // System — whether closing a slot on the Manage availability grid asks for
  // a reason first. On by default and read as `!== false` (see
  // isCloseReasonPromptEnabled); off stages Closed straight away with no
  // reason recorded. Only shown in Config when Closed is a ticked status.
  adminAskCloseReasonEnabled: true,
  // System — whether the Point of Sale screen (/pos) is open for business.
  // False leaves the route in place but serves a Coming Soon page instead, so
  // a club that has not started using the till yet does not hand staff a
  // half-set-up counter app.
  //
  // OFF by default, and read as `=== true` everywhere: a club has to stock its
  // inventory before a till is any use, so the screen stays closed until an
  // admin deliberately opens it. That read also means a config saved before
  // this setting existed — where the key is simply absent — keeps the POS
  // closed rather than exposing a half-set-up counter app.
  posEnabled: false,
  // System — whether booking confirmation emails are actually sent
  // (Config -> System).
  //
  // OFF by default, and read as `=== true` (see emailServiceUtil.js), so a
  // club that has not set up an email backend yet never fires requests at an
  // endpoint that isn't there — and so upgrading into this build cannot start
  // mailing guests from a deployment that was never sending before.
  emailSendingEnabled: false,
  // System — the symbol printed in front of every amount ('₱', '$', 'RM'),
  // and the club's own name for that currency. Display only: nothing is
  // converted, and no rate is rewritten — a club switching these is telling
  // the app what its existing numbers already mean. See utils/currencyUtil.js.
  currencySymbol: DEFAULT_CURRENCY_SYMBOL,
  currencyDescription: DEFAULT_CURRENCY_DESCRIPTION,
  contactNumber: '',
  emailAddress: '',
  facebookPage: '',
  locationText: '',
  mapsUrl: '',
  // Sports offered by this club (Config -> Sports & Courts). A single
  // default entry keeps existing single-sport deployments behaving
  // exactly as before (no sport picker shown to guests — see App.jsx's
  // hasMultipleSports check). Admins add more sports (e.g. Tennis) to
  // unlock the sport-picker and per-sport rates/courts/add-ons. Each
  // sport carries its own day/night pricing, mirroring the old flat
  // pricePeak/priceOffPeak/peakRateStart fields below (kept, for now, as
  // a legacy mirror of the default sport so older code paths that
  // haven't been made sport-aware yet keep working unchanged).
  // openPlayEnabled/openPlayPricePerPlayer/openPlayNote drive the "Join
  // Open Play" guest flow (OpenPlayModal.jsx) — pay-per-player, rotating
  // queue sessions, separate from reserving a whole court. Disabled by
  // default; an admin turns it on per sport from Config -> Sports & Courts.
  sports: [
    {
      id: 'default', name: 'Pickleball', icon: '🏓',
      peakRateStart: 16, pricePeak: 300, priceOffPeak: 200,
      // Hourly price bands for the day (Config -> Sports & Courts). Each is
      // { id, name, startHour, endHour, price, days }, startHour INCLUSIVE and
      // endHour EXCLUSIVE in 24-hour time; a band whose endHour is <= its
      // startHour wraps past midnight (17 -> 6 is 5PM through 5:59AM), which
      // is how a club prices one continuous evening/overnight block.
      // `days` holds Date.getDay() indexes (0 = Sunday) and scopes the band to
      // those weekdays only — an empty array means every day, which is what
      // keeps every band saved before day-scoping existed working unchanged.
      // Replaces the fixed two-rate peakRateStart/pricePeak/priceOffPeak
      // trio above, which is now only a legacy mirror. See getCourtRate in
      // bookingUtils.js — a config with no rateBands still prices off the
      // old fields, so nothing breaks before the migration below runs.
      rateBands: [
        { id: 'band-offpeak', name: 'Off-peak', startHour: 0, endHour: 16, price: 200, days: [] },
        { id: 'band-peak', name: 'Peak', startHour: 16, endHour: 0, price: 300, days: [] },
      ],
      openPlayEnabled: false, openPlayPricePerPlayer: 0, openPlayNote: '',
      // landingImageUrl/landingDescription feed the "Pick your court" sport
      // card on the landing page (LandingPage.jsx) — set per sport from
      // Config -> Landing Page, separate from the shared hero gallery
      // photos so each sport can show its own court image. Empty by
      // default; the landing page falls back to a placeholder + generic
      // copy when unset.
      landingImageUrl: '', landingDescription: '',
      // Optional per-sport override for the "Book a Court" hero collage on
      // the Time Slot Selection screen (App.jsx) — each entry is { id, url },
      // same shape as the top-level galleryPhotos. When a sport has its own
      // photos here, the guest sees only that sport's courts once they've
      // picked it; when empty, it falls back to the shared galleryPhotos
      // below (so a single-sport club, or a multi-sport club that hasn't
      // bothered adding sport-specific photos, keeps working unchanged).
      galleryPhotos: [],
    },
  ],
  // Individual court records (Config -> Sports & Courts) — replaces the
  // old flat "Number of Courts" count as the source of truth. Each court
  // is tagged with a sportId (must match a sports[].id) so booking
  // grids/admin screens can scope to one sport. dayRate/nightRate
  // optionally override that court's sport's default pricing (null/blank
  // = inherit). indoor is true/false/null (null = not specified).
  // Each court may also carry `bandRates: { [bandId]: price }` overriding
  // individual bands; a missing or blank entry inherits the band's price,
  // and `showBookedInitials` (default false) deciding whether a taken hour on
  // the public guest grid reads "BOOKED" or "BOOKED (XY)", where XY is derived
  // from that booking's own guest name — initials only, never a readable name.
  // See getDisplayName in TimeSlotSelection.jsx.
  //
  // A court whose type is "Add-on" (`isAddOn: true`) is a court-shaped extra
  // rather than a court — a ball machine, a coaching lane. It takes a grid
  // column and an hourly rate like any court, but a guest can only take an
  // hour on it while holding the SAME hour on one of `addOnPairedCourts`
  // (court numbers; empty = any court of its own sport), and is shown
  // `addOnRequiredMessage` when they try without one. Absent on every court
  // saved before this existed, and read as `=== true`, so those keep behaving
  // as ordinary courts. See the add-on helpers in bookingUtils.js.
  courts: [],
  // --- Legacy single-sport fields ---
  // Kept and auto-mirrored from `sports`/`courts` above (see AdminConfig's
  // Sports & Courts tab) so booking/admin screens not yet migrated to be
  // sport-aware keep working unchanged. Do not edit these directly once
  // multiple sports are configured — edit the sports/courts arrays
  // instead and they'll be kept in sync.
  numberOfCourts: 2,
  timeSlots: [
    '6-7AM', '7-8AM', '8-9AM', '9-10AM', '10-11AM', '11-12PM',
    '12-1PM', '1-2PM', '2-3PM', '3-4PM', '4-5PM', '5-6PM',
    '6-7PM', '7-8PM', '8-9PM', '9-10PM', '10-11PM', '11-12AM'
  ],
  peakRateStart: 16,
  pricePeak: 300,
  priceOffPeak: 200,
  // Optional per-court day/night rate overrides: [{ court: 1, dayRate: 320, nightRate: 350 }, ...]
  // A court without an entry (or a court whose entry omits a field) falls back to
  // priceOffPeak/pricePeak above, split at peakRateStart.
  courtRates: [],
  bookingFee: 10,
  // Whether `bookingFee` above is charged PER COURT-HOUR instead of once per
  // reservation (Config -> Pricing).
  //
  // FALSE — the original behavior — charges the flat amount once, however
  // many courts, hours or dates the reservation covers. TRUE multiplies it
  // by every court-hour booked: 2 courts × 3 hours is 6 × the fee, and a
  // multi-date reservation counts every date's hours. See
  // calculateBookingFee in bookingUtils.js.
  //
  // Read as `=== true` everywhere, so a config saved before this existed —
  // where the key is simply absent — keeps charging a flat fee.
  bookingFeePerHour: false,
  // --- Event packages (Admin -> Events and Packages) ---
  // Whole-court event bookings sold as a block ("4 hours, ₱5,000") rather
  // than by the hour: birthdays, corporate nights, tournaments.
  //
  // Stored here rather than under its own Firebase node — unlike event
  // POSTERS (/events), which carry an image each and are paginated, a
  // package is small catalog data that the guest side will need on load,
  // exactly like sports/courts/equipment/policies already are.
  //
  // Each package is:
  //   {
  //     id, name, description,
  //     // One option per DURATION, each carrying its own rate lines —
  //     // `days: []` on a rate means every day, the same convention a
  //     // sport's rate bands use. A club charging more on weekends adds a
  //     // second RATE to the 4-hour option, not a second 4-hour option.
  //     // Options written before rates existed stored a flat `price`/`days`
  //     // on the option itself; getOptionRates() reads those as one rate and
  //     // the Packages editor rewrites them on next save.
  //     options:    [{ id, hours, rates: [{ id, price, days: number[] }] }],
  //     // The other way to price a package, used when `options` is empty:
  //     // guests pick any start and end inside the window and pay by the
  //     // hour. Same shape and day convention as a sport's rate bands, and
  //     // wrapping past midnight the same way (17 -> 5 is an evening rate).
  //     // Presets win wherever both are set, so a package should have one or
  //     // the other — the Packages editor warns when it finds both.
  //     rateBands:  [{ id, startHour, endHour, price, days: number[] }],
  //     courts:     number[],   // which courts this package takes over
  //     startHour, endHour,     // bookable window, 0-23, endHour <= startHour
  //                             // wraps past midnight (15 -> 0 is 3PM-12AM)
  //     bufferMinutes,          // null = inherit packageBufferMinutes below
  //     inclusions: [{ id, text }],
  //     active,                 // false hides it from guests without deleting
  //   }
  packages: [],
  // Club-wide default cleanup/turnover gap between one package booking and
  // the next, in minutes. A package can override it (bufferMinutes above)
  // — an all-night corporate event needs a longer reset than a 2-hour
  // birthday. Applied when the guest-facing package booking flow lands;
  // authoring it here is what lets that arrive without a config migration.
  packageBufferMinutes: 60,
  // Rentable add-ons (Config -> Equipment), each tagged with a sportId —
  // replaces the old fixed paddle/trainingBalls/ballMachine list below.
  // See src/utils/equipmentAvailabilityUtil.js for how price/inventoryTotal
  // are used to track live availability.
  //
  // `rentalType` is 'session' (one charge for the whole reservation, however
  // many hours it runs) or 'hourly' (charged per booked hour, with the guest
  // choosing which of their hours to keep it for — see EquipmentRentalModal).
  // Absent on items saved before this existed and resolved to 'session', the
  // behaviour they already had. See getEquipmentRentalType in bookingUtils.js.
  equipment: [],
  // Marks config.equipment as already being in its modern shape, so the
  // legacy migration below never runs again.
  //
  // WHY THIS EXISTS: Firebase RTDB does not store empty arrays — saving
  // `equipment: []` removes the key entirely, so the next load sees no
  // `equipment` at all and cannot tell "never migrated" apart from "the
  // admin deliberately deleted every item". Without this flag, clearing
  // the Equipment tab silently resurrected the legacy Paddle / Training
  // Balls rows on the next load. A fresh deployment starts at `true`
  // because it has no legacy fields to migrate; a legacy deployment gets
  // it written the one time its migration runs.
  //
  // The legacy flat fields (paddleRentalFee, ballRentalFee,
  // paddleInventoryTotal, ballSetInventoryTotal, ...) are deliberately NOT
  // defaulted here anymore — they only ever existed as migration source
  // data for old deployments, and seeding them into a brand-new config was
  // what gave every new club two equipment rows it never asked for.
  equipmentMigrated: true,
  // Minutes a guest has to upload a payment receipt after reaching checkout
  // before the reservation is automatically released back to open.
  paymentTimeoutMinutes: 5,
  // The smallest share of the total an admin may accept up front to confirm
  // a reservation (Config -> Pricing). 0 — the default, and what every club
  // that predates this setting resolves to — means full payment is required
  // and the "Confirm (Partial)" button never appears. See
  // src/utils/partialPaymentUtil.js.
  partialPaymentPercent: DEFAULT_PARTIAL_PAYMENT_PERCENT,
  // How far past midnight the booking grids extend into the next date's
  // hours (Config -> Pricing). See DEFAULT_NEXT_DAY_GRID_END_HOUR.
  nextDayGridEndHour: DEFAULT_NEXT_DAY_GRID_END_HOUR,
  adminEmail: '',
  qrCodes: [],
  // --- Shared ownership (Config -> System) ---
  // Some clubs are co-owned: each court has its own owner who sets their
  // own rate and collects payment into their own GCash/bank account.
  //
  // FALSE is the whole existing product: one club-wide `qrCodes` list, any
  // mix of courts in one booking. Ticking it on makes `owners` below the
  // source of payment details and scopes a booking to a single owner —
  // see isSharedOwnershipEnabled and friends in bookingUtils.js. Read as
  // `=== true` everywhere so a config saved before this existed, where the
  // key is simply absent, behaves exactly as it always has.
  sharedOwnershipEnabled: false,
  // System — whether this club runs Open Play at all (Config -> System).
  //
  // FALSE by default, and read as `=== true` everywhere, so every club that
  // upgrades into this build keeps the product it already had: no hosting
  // button in the admin grid, nothing new for guests. A club that wants
  // Open Play opts in deliberately rather than discovering a half-set-up
  // feature already live on its site.
  openPlayEnabled: false,
  // System — how far ahead of today guests may book (Config -> System).
  // 30 means the booking screen offers today through 30 days from now and
  // refuses anything past that.
  //
  // The ADMIN grid is deliberately not limited by this: staff still need to
  // take a reservation further out over the phone. Read through
  // getBookingWindowDays (bookingUtils.js), which falls back to this same
  // default for a config saved before the setting existed.
  bookingWindowDays: DEFAULT_BOOKING_WINDOW_DAYS,
  // Court owners (Config -> Payment). Each is
  // { id, name, contactNumber, email, qrCodes: [{ id, name, url, accName,
  // accNumber }] } — the same QR shape as the club-wide list above, so the
  // checkout screen can render either without special-casing.
  // Only read while sharedOwnershipEnabled is true.
  owners: [],
  // Court photo gallery shown above the booking card. Each entry is
  // { id, url }. Falls back to bundled demo photos when empty.
  galleryPhotos: [],
  // Club logo shown in the landing page header/footer. Empty = placeholder icon.
  logoUrl: '',
  // Photos shown in the landing page hero collage. Each entry is { id, url }.
  // Falls back to galleryPhotos, then a placeholder graphic, when empty.
  landingImages: [],
  // Landing page "About" section (Config -> Landing Page). The club's own
  // story/origin copy. All three are optional — left blank, the section
  // falls back to the generated copy it used before these existed
  // (see LandingPage.jsx's ABOUT section), so an untouched deployment
  // looks exactly the same as it always did.
  aboutEyebrow: '',
  aboutTitle: '',
  // Free-form story. Blank lines separate paragraphs.
  aboutStory: '',
  // Photos stacked beside the About story. Each entry is { id, url }.
  // Falls back to the landing hero images (then a placeholder) when empty,
  // which is what this section used before it had its own uploads.
  aboutImages: [],
  // Landing page hero / headline (Config -> Landing Page). All optional —
  // each falls back to the copy the hero generated before these existed
  // (see LandingPage.jsx's HERO section), so an untouched deployment is
  // unchanged. `heroTags` are the small pills under the CTA buttons, one
  // plain string each, edited as a single comma-separated field.
  heroTitle: '',
  heroDescription: '',
  heroTags: [],
  // Landing page FAQ accordion (Config -> Landing Page -> FAQs). Each entry
  // is { id, question, answer }. Left empty — the default — the landing
  // page falls back to a generated list whose answers track config (rates,
  // court counts, cancellation policy); see buildDefaultFaqs in
  // bookingUtils.js. Adding entries here replaces that list entirely.
  faqs: [],
  // Landing page "Services" section (Config -> Landing Page).
  // `serviceTags` are the pill buttons under "What we offer" — one plain
  // string each, edited as a single comma-separated field.
  serviceTags: [
    'Court booking', 'Open play', 'Private court rental', 'Beginner sessions',
    'Coaching & lessons', 'Paddle rental', 'Ball rental', 'Tournaments', 'Corporate events',
  ],
  // `serviceCards` are the title/description cards below those pills —
  // same { id, title, description } shape as courtDetails.
  serviceCards: [
    { id: 'service-booking', title: 'Easy online booking', description: 'Pick your date, court and hours online, then pay and upload your receipt.' },
    { id: 'service-beginner', title: 'Beginner friendly', description: 'New players, families and first-timers are always welcome on our courts.' },
  ],
  // Payment configuration
  paymentQrUrl: '',
  bankName: '',
  accountName: '',
  accountNumber: '',
  // Court Details Section — a free-form list of { id, title, description }
  // cards (Config -> Court Details), replacing the old fixed courtSurface/
  // courtLighting/courtAmenities/operatingHours/lastBookingTime/
  // whatsIncluded fields. Clubs describe whatever actually matters to them
  // instead of being boxed into six preset labels. Rendered to guests on
  // the landing page's features grid and the booking screen's Court
  // Information accordion. See migrateLegacyCourtDetails below for how an
  // existing config's fixed fields are carried over.
  courtDetails: [
    { id: 'detail-surface', title: 'Court Surface', description: 'Specialized acrylic coatings over concrete base' },
    { id: 'detail-lighting', title: 'Lighting', description: 'Professional LED' },
    { id: 'detail-amenities', title: 'Amenities', description: 'Tables & Chairs' },
    { id: 'detail-hours', title: 'Operating Hours', description: '6:00 AM - 12:00 PM' },
    { id: 'detail-last-booking', title: 'Last Booking Time', description: '9:00 PM' },
    { id: 'detail-included', title: "What's Included", description: 'Player Chairs, Court Tables, Changing Rooms, Comfort Rooms, Parking Available' },
  ],
  // Court Rules/Policies Section — an admin-managed list of
  // { id, title, description } cards (Config -> Policies), same shape as
  // courtDetails, so a club can add or drop rules instead of being boxed
  // into the five fixed fields below. Those are kept only as migration
  // source data (see migrateLegacyPolicies) and as the fallback getPolicies
  // reads until the migration has run.
  policies: [],
  ruleFoodDrinks: 'Food and Drinks are allowed but observe CLAYGO.',
  rulePets: 'Pets are allowed to enjoy with the family.',
  ruleAttire: 'Wear comfortable athletic wear.',
  ruleSmoking: 'Strictly prohibited. No smoking or vaping allowed anywhere on the premises.',
  ruleCancellation: 'Cancellations are non-refundable and subject to rescheduling only. Requests must be made at least 12 hours prior to the scheduled time. No-shows or late cancellations cannot be rescheduled.',
  // Site-wide theme colors (hex). These override the jassen theme's CSS
  // custom properties at runtime — see the useEffect below — so the whole
  // app (guest booking flow, landing page, and admin dashboard) re-colors
  // itself without a code change. Defaults match the original jassen palette.
  themeTextColor: DEFAULT_THEME_COLORS.text,
  themeBackgroundColor: DEFAULT_THEME_COLORS.background,
  themeHeaderBackgroundColor: DEFAULT_THEME_COLORS.headerBackground,
  themeActiveColor: DEFAULT_THEME_COLORS.active,
  themeBrandColor: DEFAULT_THEME_COLORS.brand,
  // Config -> Theme -> Time Slot Button Colors. Holds only the colours an
  // admin changed — empty means every slot state keeps its original colour.
  // Shape and defaults: SLOT_BUTTON_STATUSES in bookingConst.js.
  slotButtonColors: {},
  // Config -> Theme -> UI Design: which guest booking screen design renders
  // (UI_DESIGN_VERSIONS in bookingConst.js). Version 1 by default, so a club
  // that never opens the setting keeps the screen it has.
  uiDesignVersion: DEFAULT_UI_DESIGN_VERSION,
  // Config -> Sports & Courts -> Hourly rates: the rows heading each hourly
  // rate in the guest and admin time slot grids, and which design they use.
  showRateSeparators: true,
  rateSeparatorDesign: DEFAULT_RATE_SEPARATOR_DESIGN,
  // Config -> Theme -> Open in Browser Popup: which popup Facebook / Messenger
  // guests see (BROWSER_PROMPT_VERSIONS in bookingConst.js). Version 1 by
  // default, so a club that never opens the setting keeps the original popup.
  browserPromptVersion: DEFAULT_BROWSER_PROMPT_VERSION,
  // Config -> Booking Settings. One-hour minimum, staggered hours allowed and
  // no block bookings — exactly how booking worked before the tab existed.
  // Read through getBookingSettings (bookingRulesUtil.js), never directly.
  bookingSettings: DEFAULT_BOOKING_SETTINGS,
};

// One-time upgrade path for configs saved before the sports/courts arrays
// existed: synthesizes a single default sport (from the old flat
// pricePeak/priceOffPeak/peakRateStart) and a courts[] array (from the old
// numberOfCourts count + courtRates overrides), so nothing about an
// existing client's booking behavior changes — they just now have real
// court/sport records to build on instead of a bare count. Returns null if
// no migration is needed (config already has a courts array, even an
// empty one saved by a fresh Sports & Courts tab).
const migrateLegacyCourtsConfig = (data) => {
  if (data.courts || !data.numberOfCourts) return null;

  const legacySport = {
    id: 'default',
    name: 'Pickleball',
    icon: '🏓',
    peakRateStart: data.peakRateStart ?? DEFAULT_CONFIG.sports[0].peakRateStart,
    pricePeak: data.pricePeak ?? DEFAULT_CONFIG.sports[0].pricePeak,
    priceOffPeak: data.priceOffPeak ?? DEFAULT_CONFIG.sports[0].priceOffPeak,
    openPlayEnabled: false,
    openPlayPricePerPlayer: 0,
    openPlayNote: '',
    landingImageUrl: '',
    landingDescription: '',
    galleryPhotos: [],
  };
  const legacyCourts = Array.from({ length: data.numberOfCourts }, (_, i) => {
    const number = i + 1;
    const oldRate = (data.courtRates || []).find(r => Number(r.court) === number) || {};
    return {
      id: `court-${number}`,
      number,
      // Blank = fall back to "Court {number}" wherever this court is
      // displayed (see getCourtLabel in bookingUtils.js).
      name: '',
      sportId: 'default',
      // false = temporarily closed (maintenance/resurfacing): the court
      // still shows on the guest grid but every hour renders greyed out
      // and unbookable. Defaults true, and anything undefined is treated
      // as available too (see isCourtAvailable in bookingUtils.js), so
      // configs saved before this field existed keep working unchanged.
      available: true,
      indoor: null,
      dayRate: oldRate.dayRate ?? null,
      nightRate: oldRate.nightRate ?? null,
    };
  });

  return { sports: [legacySport], courts: legacyCourts };
};

// One-time upgrade path for configs saved before config.equipment existed:
// turns the old fixed paddle/trainingBalls/ballMachine fields into real
// equipment[] entries, assigned to the (migrated-or-existing) first sport.
// Ids are kept identical to RENTAL_EQUIPMENT's ('paddle', 'trainingBalls',
// 'ballMachine') on purpose — existing equipmentHolds/equipmentManualUsage
// Firebase nodes for in-flight bookings are keyed by these ids, so
// changing them here would silently orphan any already-claimed rentals.
// `sportsAfterMigration` is whatever migrateLegacyCourtsConfig just
// produced (or the existing config.sports), so both migrations agree on
// which sport id the legacy single-sport data belongs to.
//
// Runs only for a config that is genuinely legacy: it still carries at
// least one of the old flat fields AND has never been stamped
// equipmentMigrated. `data.equipment` alone is not a sufficient guard,
// because Firebase RTDB drops empty arrays — an admin who removes every
// equipment item stores no `equipment` key at all, and used to get the
// legacy rows handed back to them on the next load.
const migrateLegacyEquipmentConfig = (data, sportsAfterMigration) => {
  if (data.equipment || data.equipmentMigrated) return null;

  // Nothing to migrate from: a config saved without the old flat fields is
  // already modern (or brand new), so an empty equipment list is intentional.
  const hasLegacyFields = RENTAL_EQUIPMENT.some(
    ({ configKey, inventoryKey }) => data[configKey] !== undefined || data[inventoryKey] !== undefined
  );
  if (!hasLegacyFields) return { equipmentMigrated: true };

  const defaultSportId = sportsAfterMigration?.[0]?.id || 'default';
  const equipment = RENTAL_EQUIPMENT.map(({ id, label, unit, configKey, defaultPrice, inventoryKey, defaultInventory }) => ({
    id,
    sportId: defaultSportId,
    label,
    unit,
    price: data[configKey] ?? defaultPrice,
    inventoryTotal: data[inventoryKey] ?? defaultInventory,
  }));

  // Stamped alongside the migrated list so this only ever happens once,
  // even after the admin later empties the list again.
  return { equipment, equipmentMigrated: true };
};

// One-time upgrade for configs saved before config.courtDetails existed:
// folds the old fixed Court Details fields into the new title/description
// card list, preserving whatever the club had typed. Fields left blank are
// skipped rather than becoming empty cards. Returns null when no migration
// is needed (courtDetails already present, even as an empty array — an
// admin who deliberately deleted every card must not have them restored).
const migrateLegacyCourtDetails = (data) => {
  if (data.courtDetails) return null;

  const legacyFields = [
    { id: 'detail-setup', title: 'Court Setup', value: data.courtSetup },
    { id: 'detail-surface', title: 'Court Surface', value: data.courtSurface },
    { id: 'detail-lighting', title: 'Lighting', value: data.courtLighting },
    { id: 'detail-amenities', title: 'Amenities', value: data.courtAmenities },
    { id: 'detail-hours', title: 'Operating Hours', value: data.operatingHours },
    { id: 'detail-last-booking', title: 'Last Booking Time', value: data.lastBookingTime },
    { id: 'detail-included', title: "What's Included", value: data.whatsIncluded },
  ];

  const courtDetails = legacyFields
    .filter(f => typeof f.value === 'string' && f.value.trim())
    .map(({ id, title, value }) => ({ id, title, description: value.trim() }));

  // Nothing worth carrying over — fall back to the defaults rather than
  // handing the admin an empty tab.
  return { courtDetails: courtDetails.length > 0 ? courtDetails : DEFAULT_CONFIG.courtDetails };
};

/**
 * One-time upgrade to the rate-band pricing model for configs saved when a
 * sport had exactly two rates (peakRateStart + priceOffPeak + pricePeak) and
 * a court could override them with dayRate/nightRate.
 *
 * The old rule was `hour >= peakRateStart ? pricePeak : priceOffPeak`, which
 * is exactly an off-peak band of [0, peakRateStart) plus a peak band running
 * from peakRateStart and wrapping to midnight — so prices are unchanged, they
 * are just expressed in the new shape and become editable/extendable.
 *
 * Returns null when nothing needs migrating (every sport already has bands).
 *
 * @param {object} data raw config from Firebase
 * @returns {{sports: Array, courts: Array}|null}
 */
const migrateLegacyRateBands = (data) => {
  const sports = data?.sports || [];
  if (sports.length === 0) return null;
  if (sports.every(s => Array.isArray(s.rateBands) && s.rateBands.length > 0)) return null;

  const migratedSports = sports.map((sport) => {
    if (Array.isArray(sport.rateBands) && sport.rateBands.length > 0) return sport;
    const split = Number(sport.peakRateStart ?? data.peakRateStart ?? 16);
    const offPeak = Number(sport.priceOffPeak ?? data.priceOffPeak) || 0;
    const peak = Number(sport.pricePeak ?? data.pricePeak) || 0;
    // A split of 0 meant "peak all day", so it collapses to a single band
    // rather than an empty off-peak one.
    // days: [] — the old two-rate model had no notion of weekdays, so every
    // migrated band applies every day.
    const rateBands = split === 0
      ? [{ id: 'band-peak', name: 'Peak', startHour: 0, endHour: 0, price: peak, days: [] }]
      : [
        { id: 'band-offpeak', name: 'Off-peak', startHour: 0, endHour: split, price: offPeak, days: [] },
        { id: 'band-peak', name: 'Peak', startHour: split, endHour: 0, price: peak, days: [] },
      ];
    return { ...sport, rateBands };
  });

  // Carry each court's day/night override onto the matching new band, so a
  // court priced differently from its sport keeps that difference.
  const migratedCourts = (data?.courts || []).map((court) => {
    if (court.bandRates) return court;
    const hasDay = court.dayRate !== undefined && court.dayRate !== null && court.dayRate !== '';
    const hasNight = court.nightRate !== undefined && court.nightRate !== null && court.nightRate !== '';
    if (!hasDay && !hasNight) return court;
    const bandRates = {};
    if (hasDay) bandRates['band-offpeak'] = Number(court.dayRate);
    if (hasNight) bandRates['band-peak'] = Number(court.nightRate);
    return { ...court, bandRates };
  });

  return { sports: migratedSports, courts: migratedCourts };
};

/**
 * One-time upgrade of the five fixed rule fields into policy cards, so the
 * Policies tab can add and remove rows like Court Details does. Titles match
 * the headings guests already saw on the booking screen, and any field the
 * club left blank is skipped rather than becoming an empty card.
 *
 * Returns null when nothing needs migrating.
 *
 * @param {object} data raw config from Firebase
 * @returns {{policies: Array}|null}
 */
const migrateLegacyPolicies = (data) => {
  if (Array.isArray(data?.policies) && data.policies.length > 0) return null;

  const legacy = [
    { key: 'ruleFoodDrinks', id: 'policy-food', title: 'Food & Drinks' },
    { key: 'rulePets', id: 'policy-pets', title: 'Pets' },
    { key: 'ruleAttire', id: 'policy-attire', title: 'Attire' },
    { key: 'ruleSmoking', id: 'policy-smoking', title: 'Smoking' },
    { key: 'ruleCancellation', id: 'policy-cancellation', title: 'Cancellation Policy' },
  ];
  const policies = legacy
    .filter(f => (data?.[f.key] || '').trim())
    .map(f => ({ id: f.id, title: f.title, description: data[f.key] }));

  return policies.length > 0 ? { policies } : null;
};

/**
 * Applies every legacy-shape migration to a raw config snapshot and merges the
 * result over DEFAULT_CONFIG.
 *
 * Split out of the load effect below so the identical logic can run against a
 * cached config and a freshly-read one without the two drifting apart. Pure —
 * it decides what the config should look like and reports whether anything
 * changed, but writes nothing.
 *
 * @param {object} data - the raw config value as stored in Firebase.
 * @returns {{merged: object, migrated: boolean}} the config to render, and
 *   whether a migration actually changed it (i.e. it needs persisting).
 */
const applyConfigMigrations = (data) => {
  const courtsMigration = migrateLegacyCourtsConfig(data);
  const equipmentMigration = migrateLegacyEquipmentConfig(data, courtsMigration?.sports || data.sports);
  const courtDetailsMigration = migrateLegacyCourtDetails(data);
  // Runs last and against the post-courts-migration shape, since it needs the
  // sports/courts arrays those may have just synthesized.
  const rateBandMigration = migrateLegacyRateBands({ ...data, ...courtsMigration });
  const policyMigration = migrateLegacyPolicies(data);

  return {
    merged: {
      ...DEFAULT_CONFIG,
      ...data,
      ...courtsMigration,
      ...equipmentMigration,
      ...courtDetailsMigration,
      ...rateBandMigration,
      ...policyMigration,
    },
    migrated: Boolean(
      courtsMigration || equipmentMigration || courtDetailsMigration
      || rateBandMigration || policyMigration,
    ),
  };
};

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The visitor's cached config (IndexedDB), read on mount WITHOUT waiting for
  // sign-in. Feeds landingConfig only — `config` itself stays reserved for the
  // live read, which admin screens copy into forms they save back.
  const [earlyConfig, setEarlyConfig] = useState(null);
  // The database rules require a signed-in user, and the sign-in waits on
  // App Check — so the live read below cannot start until this flips.
  const { ready: authReady } = useAuthStatus();

  // The build-time snapshot, merged over the defaults like any other config.
  // Its landing images are static files (public/snapshot/*). A club with no
  // logo in the snapshot falls back to the last logo this browser saw, then to
  // the club's own public/logo.png (the favicon, per client).
  const snapshotConfig = useMemo(() => {
    const snapshot = readBuildSnapshot();
    if (!snapshot) return null;
    const { merged } = applyConfigMigrations(snapshot);
    return { ...merged, logoUrl: merged.logoUrl || getCachedLogoUrl() || `${process.env.PUBLIC_URL}/logo.png` };
  }, []);

  // What the landing page renders from: the live config once it is in,
  // otherwise this visitor's cached copy (newer), otherwise the build
  // snapshot. Null when there is nothing yet — the app then keeps its
  // loading screen, as before snapshots existed.
  //
  // Images the admin has not changed since the deploy are pointed at their
  // static files (see applySnapshotImages), so photos do not blink when the
  // live config replaces the snapshot, and repeat visits use the HTTP cache.
  const landingConfig = useMemo(() => {
    const base = !loading ? config : (earlyConfig || snapshotConfig);
    return snapshotConfig ? applySnapshotImages(base, CONFIG_SNAPSHOT.images) : base;
  }, [loading, config, earlyConfig, snapshotConfig]);

  // Bumped by reloadConfig() to tear the subscription below down and open a
  // fresh one. Needed because onValue on an unreachable database never calls
  // back at all — not the success handler and not the error one — so the only
  // way to genuinely retry is to re-subscribe, not to wait longer.
  const [reloadKey, setReloadKey] = useState(0);

  /**
   * Re-runs the config load from scratch. Wired to the Try Again button on
   * the connection-timeout screen (App.jsx); safe to call at any time.
   *
   * @returns {void}
   */
  const reloadConfig = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  // Read the cached config straight away so a returning visitor's landing page
  // does not wait for App Check + sign-in. Fails soft: no cache just means the
  // snapshot (or the loading screen) is used instead.
  useEffect(() => {
    let cancelled = false;
    readCachedConfig()
      .then((cached) => {
        if (!cancelled && cached) setEarlyConfig(applyConfigMigrations(cached.config).merged);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Load config on mount, and again on every reloadConfig() — revalidating a
  // locally cached copy rather than re-downloading it.
  //
  // The live listener is on `config/lastModified`, a single number, instead of
  // the whole node. The full config is only read when that stamp differs from
  // the cached one, nothing is cached, or the cached copy has aged past
  // CONFIG_CACHE_MAX_AGE_MS. An admin saving config bumps the stamp, so every
  // open tab still picks the change up live — what changes is that a visitor
  // whose cache is already current pays for one number instead of several
  // megabytes of base64 images (logo, QR codes, gallery/landing/about photos).
  //
  // A config carrying no lastModified — never saved since this was introduced,
  // or edited straight from the Firebase console — always takes the full read,
  // which is exactly the behaviour this replaced. Nothing is ever served
  // against a stamp that does not exist.
  useEffect(() => {
    if (!db) {
      setLoading(false);
      return undefined;
    }
    // Wait for sign-in: a read issued before it is denied by the rules. The
    // effect re-runs when authReady flips.
    if (!authReady) {
      setLoading(true);
      return undefined;
    }

    // Back to a loading state on a retry — a no-op on the first run, where
    // both are already at these values.
    setLoading(true);
    setError(null);

    // Makes the teardown authoritative: an in-flight read that lands after
    // this effect has been torn down must not set state or write a cache
    // entry for a subscription that no longer exists.
    let cancelled = false;

    /**
     * Reads the whole config node, renders it, and caches it.
     *
     * @param {number|null} stamp - the lastModified value this read belongs to,
     *   stored alongside the config so the next visit can compare against it.
     * @returns {Promise<void>}
     */
    const fetchFullConfig = async (stamp) => {
      const snapshot = await get(ref(db, 'config'));
      if (cancelled) return;

      // The rules let only super-admins write /config. Everyone else (every
      // guest, every other admin) renders what is in hand and leaves
      // persisting to a super-admin, rather than firing a write that is denied.
      const canWriteConfig = isSuperAdmin(auth?.currentUser?.email);

      if (!snapshot.exists()) {
        // Initialize with defaults if no config exists. Stamped on the way in
        // so the very first read by anyone else is already cacheable. Until
        // a super-admin does this, visitors run on DEFAULT_CONFIG in memory.
        if (canWriteConfig) {
          await set(ref(db, 'config'), { ...DEFAULT_CONFIG, lastModified: Date.now() })
            .catch(err => { console.error('Error initializing config:', err); });
        }
        return;
      }

      const data = snapshot.val();
      const { merged, migrated } = applyConfigMigrations(data);
      setConfig(merged);

      if (migrated) {
        // Persist so these migrations only ever run once per deployment. The
        // new stamp invalidates every other visitor's cache along with it, and
        // the resulting listener fire re-enters here to cache the settled
        // shape — deliberately not cached now, since what is in hand has not
        // been written yet.
        //
        // Super-admins only (see canWriteConfig). Everyone else keeps
        // rendering the migrated shape in memory until a super-admin next
        // loads the app or saves from an admin screen — updateConfig writes
        // the merged config, so either one persists the migration.
        if (canWriteConfig) {
          set(ref(db, 'config'), { ...merged, lastModified: Date.now() }).catch(err => {
            console.error('Error migrating config to sports/courts/equipment:', err);
          });
        }
        return;
      }

      await writeCachedConfig(data, stamp);
    };

    const unsubscribe = onValue(ref(db, 'config/lastModified'), (snapshot) => {
      const raw = snapshot.exists() ? Number(snapshot.val()) : NaN;
      const stamp = Number.isFinite(raw) ? raw : null;

      (async () => {
        try {
          const cached = stamp === null ? null : await readCachedConfig();
          if (cancelled) return;

          const current = cached
            && cached.lastModified === stamp
            && Date.now() - (cached.cachedAt || 0) < CONFIG_CACHE_MAX_AGE_MS;

          if (current) {
            // A cached config is always post-migration (see fetchFullConfig),
            // so `migrated` here means a NEWER BUILD has added a migration the
            // cached shape predates. That has to be written, not just
            // rendered, so it falls through to the full read.
            const { merged, migrated } = applyConfigMigrations(cached.config);
            if (!migrated) {
              setConfig(merged);
              return;
            }
          }

          await fetchFullConfig(stamp);
        } catch (err) {
          console.error('Error loading config:', err);
          if (!cancelled) setError(err.message);
          // A cache that cannot be trusted is worse than no cache at all.
          clearCachedConfig();
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, (err) => {
      console.error('Error loading config:', err);
      setError(err.message);
      setLoading(false);
    });

    return () => { cancelled = true; unsubscribe(); };
  }, [reloadKey, authReady]);

  // Mirror the club logo into localStorage so loading screens can show it —
  // AuthGuard.js shows its spinner before any config exists (it gates on
  // sign-in), and the config-loading spinner runs while `config` is still
  // DEFAULT_CONFIG, so neither can read the real logo directly.
  // See cacheLogoUrl/getCachedLogoUrl in bookingUtils.js.
  //
  // The `loading` guard is essential, not cosmetic: DEFAULT_CONFIG.logoUrl
  // is '', so without it this effect fired on mount with an empty string
  // and cacheLogoUrl() cleared the stored logo — wiping the cache at
  // exactly the moment the loading spinner wanted to read it. Only sync
  // once real config has arrived; an empty logoUrl then genuinely means
  // the admin removed it, which should clear the cache.
  useEffect(() => {
    if (loading) return;
    cacheLogoUrl(config?.logoUrl);
  }, [loading, config?.logoUrl]);

  // The landing page can render from the cache or the build snapshot before
  // the live config lands, so the theme follows whatever it is showing.
  const themeSource = landingConfig || config;

  // Push the configurable theme colors into a dynamically-injected
  // stylesheet that overrides src/styles/global.css's :root custom properties.
  // Runs on every config change (including the very first load, so the
  // defaults above take effect even before any admin edits them).
  useEffect(() => {
    const pick = (value, fallback) => (isValidHex(value) ? value.trim() : fallback);
    const textColor = pick(themeSource.themeTextColor, DEFAULT_CONFIG.themeTextColor);
    const bgColor = pick(themeSource.themeBackgroundColor, DEFAULT_CONFIG.themeBackgroundColor);
    const headerBg = pick(themeSource.themeHeaderBackgroundColor, DEFAULT_CONFIG.themeHeaderBackgroundColor);
    const activeColor = pick(themeSource.themeActiveColor, DEFAULT_CONFIG.themeActiveColor);
    const brandColor = pick(themeSource.themeBrandColor, DEFAULT_CONFIG.themeBrandColor);
    const activeRgb = hexToRgb(activeColor);

    let styleTag = document.getElementById('dynamic-theme-vars');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'dynamic-theme-vars';
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = `:root {
      --jt-ink: ${textColor};
      --jt-bg: ${bgColor};
      --jt-header-bg: ${headerBg};
      --jt-cyan: ${activeColor};
      /* Raw channels so a stylesheet can build any alpha it needs off the
         configured colour — rgba(var(--jt-cyan-rgb), .32) — instead of
         hardcoding a tint that then ignores the club's theme. */
      --jt-cyan-rgb: ${activeRgb.r}, ${activeRgb.g}, ${activeRgb.b};
      --jt-cyan-deep: ${shadeHex(activeColor, -12)};
      --jt-cyan-soft: ${hexToRgba(activeColor, 0.15)};
      --jt-brand: ${brandColor};
      ${buildSlotColorCssVars(themeSource.slotButtonColors)}
    }`;
  }, [
    themeSource.themeTextColor,
    themeSource.themeBackgroundColor,
    themeSource.themeHeaderBackgroundColor,
    themeSource.themeActiveColor,
    themeSource.themeBrandColor,
    themeSource.slotButtonColors,
  ]);

  /**
   * Writes the config back to Firebase, stamped so cached visitors revalidate.
   *
   * The stamp is what makes the client-side cache correct: a config saved
   * without one leaves every cached visitor on the previous version until
   * CONFIG_CACHE_MAX_AGE_MS elapses. It is applied here rather than by callers
   * so that no save path can forget it.
   *
   * The local cache is refreshed in the same breath, so the admin who just
   * saved does not turn around and re-download what they already hold.
   *
   * @param {object} newConfig - fields to merge over the current config.
   * @returns {Promise<object>} the config as saved, including its new stamp.
   * @throws {Error} when the database is unconfigured, or the write fails.
   */
  const updateConfig = async (newConfig) => {
    if (!db) {
      throw new Error('Database not configured');
    }

    const lastModified = Date.now();
    const updatedConfig = { ...config, ...newConfig, lastModified };

    try {
      await set(ref(db, 'config'), updatedConfig);
    } catch (error) {
      // Never leave a cache entry describing a write that did not land.
      await clearCachedConfig();
      throw error;
    }

    setConfig(updatedConfig);
    await writeCachedConfig(updatedConfig, lastModified);
    return updatedConfig;
  };

  // Add a new QR code
  const addQRCode = async (name, url, accName, accNumber) => {
    const newQRCode = { name, url, accName, accNumber, id: Date.now().toString() };
    const updatedQRCodes = [...config.qrCodes, newQRCode];
    await updateConfig({ qrCodes: updatedQRCodes });
  };

  // Remove a QR code
  const removeQRCode = async (id) => {
    const updatedQRCodes = config.qrCodes.filter(qr => qr.id !== id);
    await updateConfig({ qrCodes: updatedQRCodes });
  };

  // Add a gallery photo
  const addGalleryPhoto = async (url) => {
    const newPhoto = { id: Date.now().toString(), url };
    const updatedPhotos = [...(config.galleryPhotos || []), newPhoto];
    await updateConfig({ galleryPhotos: updatedPhotos });
  };

  // Remove a gallery photo
  const removeGalleryPhoto = async (id) => {
    const updatedPhotos = (config.galleryPhotos || []).filter(p => p.id !== id);
    await updateConfig({ galleryPhotos: updatedPhotos });
  };

  // Add a landing-page hero photo
  const addLandingImage = async (url) => {
    const newPhoto = { id: Date.now().toString(), url };
    const updatedPhotos = [...(config.landingImages || []), newPhoto];
    await updateConfig({ landingImages: updatedPhotos });
  };

  // Remove a landing-page hero photo
  const removeLandingImage = async (id) => {
    const updatedPhotos = (config.landingImages || []).filter(p => p.id !== id);
    await updateConfig({ landingImages: updatedPhotos });
  };

  // Add a photo to the landing page's About section (stacked beside the
  // club's story) — same shape/mechanics as addLandingImage.
  const addAboutImage = async (url) => {
    const newPhoto = { id: Date.now().toString(), url };
    await updateConfig({ aboutImages: [...(config.aboutImages || []), newPhoto] });
  };

  // Remove an About-section photo
  const removeAboutImage = async (id) => {
    await updateConfig({ aboutImages: (config.aboutImages || []).filter(p => p.id !== id) });
  };

  // Add a photo to one sport's own gallery override (Config -> Photos, once
  // there's more than one sport) — same shape/mechanics as addGalleryPhoto,
  // just writing into that sport's entry in config.sports instead of the
  // shared top-level galleryPhotos. See App.jsx's gallery-selection logic
  // for how a sport with its own photos here takes priority over the
  // shared gallery.
  const addSportGalleryPhoto = async (sportId, url) => {
    const newPhoto = { id: Date.now().toString(), url };
    const sports = (config.sports || []).map(s => (
      s.id === sportId ? { ...s, galleryPhotos: [...(s.galleryPhotos || []), newPhoto] } : s
    ));
    await updateConfig({ sports });
  };

  // Remove a photo from one sport's own gallery override.
  const removeSportGalleryPhoto = async (sportId, id) => {
    const sports = (config.sports || []).map(s => (
      s.id === sportId ? { ...s, galleryPhotos: (s.galleryPhotos || []).filter(p => p.id !== id) } : s
    ));
    await updateConfig({ sports });
  };

  const value = {
    config,
    loading,
    landingConfig,
    error,
    reloadConfig,
    updateConfig,
    addQRCode,
    removeQRCode,
    addGalleryPhoto,
    removeGalleryPhoto,
    addSportGalleryPhoto,
    removeSportGalleryPhoto,
    addLandingImage,
    removeLandingImage,
    addAboutImage,
    removeAboutImage
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * Re-provides the config context with a different `config` for the subtree
 * below it; every other field (loading, landingConfig, updateConfig, ...) is
 * passed through unchanged.
 *
 * Used by the guest booking screens (App.jsx) to render from the cached or
 * build-time snapshot config while the live config is still loading, without
 * touching what admin screens see — they sit outside this override and keep
 * reading the live `config`, which they copy into forms they save back.
 *
 * @param {object} props
 * @param {object} props.config - the config consumers below should see.
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function ConfigOverride({ config, children }) {
  const parent = useContext(ConfigContext);
  const value = useMemo(
    () => (parent && config && parent.config !== config ? { ...parent, config } : parent),
    [parent, config],
  );
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export default ConfigContext;
